import Skeleton from "react-loading-skeleton";
import { GuestCard } from "../../../../../components/cards/guestCard";
import { Icon } from "../../../../../components/icon/icon";
import { Pill } from "../../../../../components/pill";
import { TableComponent } from "../../../../../components/table";
import { BgWrapper } from "../../../../../components/wrapper/bgWrapper";
import { useAdminGetEventGuestQuery } from "../../../../../redux/features/admin/event/events.api";
import {
  currencyShorts,
  PRODUCT_PRICING,
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../../../utils/constants";
import {
  calcTotalTicketPrice,
  formatDate,
  formatToCurrency,
} from "../../../../../utils/helpers";
import { TicketSalesBreakdown } from "../../../../../utils/types";

export const AdminEventGuestView = ({
  guestId,
  ticketBreakdown,
}: {
  guestId: string;
  ticketBreakdown: TicketSalesBreakdown;
}) => {
  const { data: guest, isFetching: fetchingGuestDetails } =
    useAdminGetEventGuestQuery(
      {
        id: guestId,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: guestId === "",
      },
    );

  return (
    <>
      <BgWrapper className="flex flex-col gap-4">
        {fetchingGuestDetails ? (
          <Skeleton
            width={"100%"}
            height={48}
            baseColor={SKELETON_BASE_COLOR}
            highlightColor={SKELETON_HIGHLIGHT_COLOR}
          />
        ) : (
          <GuestCard
            className=""
            name={`${guest?.data?.firstName} ${guest?.data?.lastName}`}
            email={`${guest?.data?.email}`}
            showTicket
            guest={guest?.data}
            ticketColor={ticketBreakdown.map((tb) => {
              return {
                ticketId: tb.ticketId,
                color: tb.color,
              };
            })}
          />
        )}

        <TableComponent
          headers={[]}
          rows={[
            {
              id: 1,
              items: [
                <div className="flex items-center gap-2">
                  <p className="flex-1 font-bold text-grey-500">
                    {guest?.data?.tickets?.reduce((acc, el) => {
                      return acc + el.quantity;
                    }, 0)}{" "}
                    {guest?.data?.tickets?.reduce((acc, el) => {
                      return acc + el.quantity;
                    }, 0) === 1
                      ? "Ticket"
                      : "Tickets"}
                  </p>

                  <p>
                    <span className="font-bold text-primary">
                      {formatToCurrency(
                        calcTotalTicketPrice(
                          guest?.data?.tickets?.reduce((acc, el) => {
                            return (
                              acc + (el?.ticketId?.price ?? 0) * el.quantity
                            );
                          }, 0) ?? 0,
                          "NGN",
                          guest?.data?.discount?.value,
                          guest?.data?.discount?.type,
                          false,
                        ),
                        {
                          currency: guest?.data?.eventId?.currency ?? "NGN",
                        },
                      )}
                    </span>{" "}
                    {guest?.data?.discount && (
                      <span className="text-grey-500 line-through">
                        {formatToCurrency(
                          calcTotalTicketPrice(
                            guest?.data?.tickets?.reduce((acc, el) => {
                              return (
                                acc + (el?.ticketId?.price ?? 0) * el.quantity
                              );
                            }, 0) ?? 0,
                            "NGN",
                            undefined,
                            undefined,
                            false,
                          ),
                          {
                            currency: guest?.data?.eventId?.currency ?? "NGN",
                          },
                        )}
                      </span>
                    )}
                  </p>
                </div>,
              ],
            },
            {
              id: 2,
              items: [
                guest?.data?.discount ? (
                  <div className="flex items-center gap-1">
                    <p className="flex-1 font-bold text-grey-500">
                      Discount Applied
                    </p>

                    <Icon
                      svg="ticket-percent"
                      width={24}
                      height={24}
                      className="text-blue"
                    />

                    <p className="font-bold uppercase text-primary">
                      {guest?.data?.discount?.name}
                    </p>

                    <p>•</p>

                    <p className="font-bold text-primary">
                      {guest?.data?.discount?.type === "amount"
                        ? `${formatToCurrency(guest?.data?.discount?.value, {
                            currency: guest?.data?.eventId?.currency ?? "NGN",
                          })} off`
                        : ""}
                      {guest?.data?.discount?.type === "percentage"
                        ? `${guest?.data?.discount?.value}% off`
                        : ""}
                    </p>
                  </div>
                ) : undefined,
              ],
            },
            {
              id: 3,
              items: [
                <div className="flex items-center gap-[2px]">
                  <p className="font-bold text-grey-500">Our Fee</p>

                  <Pill
                    text={
                      guest?.data?.payment?.feeDescription ??
                      `${PRODUCT_PRICING[guest?.data?.eventId?.currency ?? "NGN"].feeText} FEE`
                    }
                    variation="grey"
                  />

                  <p className="flex-1 text-right font-bold text-primary">
                    {formatToCurrency(guest?.data?.payment?.fees ?? 0, {
                      currency:
                        currencyShorts[guest?.data?.eventId?.currency ?? "NGN"],
                    })}
                  </p>
                </div>,
              ],
            },
            {
              id: 4,
              items: [
                <div className="flex items-center gap-[2px]">
                  <p className="flex-1 font-bold text-grey-500">
                    Date Registered
                  </p>

                  <p className="font-bold text-primary">
                    {formatDate(
                      guest?.data.createdAt,
                      "MMMM DD, YYYY • h:mm A",
                    )}
                  </p>
                </div>,
              ],
            },
          ]}
          isLoading={fetchingGuestDetails}
          loadingRows={4}
          onRowClick={() => {}}
          isRowClickable={false}
          isTableEmpty={false}
        />
      </BgWrapper>

      {!fetchingGuestDetails &&
        guest?.data?.invitedGuests?.map((invitedGuest, idx) => {
          return (
            <BgWrapper key={`innvitedGuest-${idx}`}>
              <GuestCard
                name={`${invitedGuest?.firstName} ${invitedGuest?.lastName}`}
                email={`${invitedGuest?.email}`}
              />
            </BgWrapper>
          );
        })}
    </>
  );
};
